import { useCallback, useState } from "react";
import { useDebounce } from "../../util/use-debounce";
import { DeadLetterApi, DeadLetterApiError } from "../api/dead-letter.api";
import {
  DeadLetterFilters,
  DeadLetterPage,
  EMPTY_DEAD_LETTER_FILTERS,
} from "../model/dead-letter";

export const FORBIDDEN_MESSAGE =
  "You need the ADMIN role to use the Dead Letters page.";

export const UNAUTHORIZED_MESSAGE =
  "Your session has expired or you are not logged in. Log in again to use the Dead Letters page.";

export interface ResendResult {
  id: number;
  success: boolean;
  message: string;
}

interface UseDeadLettersPage {
  data: DeadLetterPage | undefined;
  loading: boolean;
  error: string | undefined;
  forbidden: boolean;
  page: number;
  rowsPerPage: number;
  filters: DeadLetterFilters;
  debouncedFilters: DeadLetterFilters;
  resendingId: number | undefined;
  resendResult: ResendResult | undefined;
  fetchData: (
    pageNumber: number,
    pageSize: number,
    filters: DeadLetterFilters
  ) => Promise<void>;
  handleChangePage: (newPage: number) => void;
  handleChangeRowsPerPage: (rowsPerPage: number) => void;
  handleFilterChange: (change: Partial<DeadLetterFilters>) => void;
  resend: (id: number) => Promise<void>;
  clearResendResult: () => void;
}

export function describeResendError(e: unknown): string {
  if (e instanceof DeadLetterApiError) {
    if (e.unauthorized) {
      return UNAUTHORIZED_MESSAGE;
    }
    if (e.forbidden) {
      return FORBIDDEN_MESSAGE;
    }
    if (e.status === 400 || e.status === 409) {
      return `Resend refused: ${e.message}`;
    }
    if (e.status === 404) {
      return "Resend failed: the dead letter no longer exists.";
    }
    return `Resend failed: ${e.message}`;
  }
  return "Resend failed: unexpected error.";
}

export const useDeadLettersPage: () => UseDeadLettersPage = () => {
  const [data, setData] = useState<DeadLetterPage>();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [forbidden, setForbidden] = useState(false);
  const [filters, setFilters] = useState<DeadLetterFilters>(
    EMPTY_DEAD_LETTER_FILTERS
  );
  const debouncedFilters = useDebounce(filters, 300);
  const [resendingId, setResendingId] = useState<number>();
  const [resendResult, setResendResult] = useState<ResendResult>();

  const handleChangePage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  }, []);

  const handleFilterChange = useCallback(
    (change: Partial<DeadLetterFilters>) => {
      setFilters((current) => ({ ...current, ...change }));
      setPage(0);
    },
    []
  );

  const fetchData = useCallback(
    async (
      pageNumber: number,
      pageSize: number,
      currentFilters: DeadLetterFilters
    ) => {
      try {
        setLoading(true);
        const response = await DeadLetterApi.getDeadLetters({
          pageNumber,
          pageSize,
          filters: currentFilters,
        });
        setData(response);
        setError(undefined);
        setForbidden(false);
      } catch (e) {
        if (e instanceof DeadLetterApiError && e.unauthorized) {
          setForbidden(true);
          setError(UNAUTHORIZED_MESSAGE);
        } else if (e instanceof DeadLetterApiError && e.forbidden) {
          setForbidden(true);
          setError(FORBIDDEN_MESSAGE);
        } else {
          setError(
            `Could not load dead letters: ${
              e instanceof Error ? e.message : "unexpected error"
            }`
          );
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const resend = useCallback(async (id: number) => {
    setResendingId(id);
    try {
      const success = await DeadLetterApi.resend(id);
      setResendResult({
        id,
        success,
        message: success
          ? `Dead letter ${id} was resent to its original topic.`
          : `Dead letter ${id} was not resent.`,
      });
    } catch (e) {
      setResendResult({ id, success: false, message: describeResendError(e) });
    } finally {
      setResendingId(undefined);
    }
  }, []);

  const clearResendResult = useCallback(() => setResendResult(undefined), []);

  return {
    data,
    loading,
    error,
    forbidden,
    page,
    rowsPerPage,
    filters,
    debouncedFilters,
    resendingId,
    resendResult,
    fetchData,
    handleChangePage,
    handleChangeRowsPerPage,
    handleFilterChange,
    resend,
    clearResendResult,
  };
};
